import React, { useContext, useState } from 'react';
import { Box, Button, CardMedia, Theme } from '@mui/material';
import {
  IMembershipType,
  IProductCode,
  IProductsQuery,
  ProductsDocument,
  useCreateProductMutation,
  useMembershipTypeOptionsQuery,
  useProductCodesQuery
} from '../../generated/graphql';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import DialogBox from '../Common/Layout/DialogBox';
import { AppContext } from '../../AppContext';
import { ApolloError } from '@apollo/client';
import { sameProductNameExists } from '../../utils/clientUtils';
import { Actions } from '../../enums/ActionEnums';
import InputComponent from '../Common/Fields/InputComponent';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import { RB_NAME } from '../../enums/MembershipEnums';
import { Colors } from '../../utils/theme';
import EmailBannerUploader, { ImageContainerMargins } from './EmailBannerUploader';

interface IProps {
  handleClose: () => void;
}

export type IErrors = {
  name: string | null;
  productCode: string | null;
  price: string | null;
  vatRange: string | null;
  updateError?: ApolloError;
};

export const defaultErrorsObj: IErrors = {
  name: null,
  productCode: null,
  price: null,
  vatRange: null
};

export const ErrorTexts = {
  name: 'Name is required',
  nameExists: 'Name already exists',
  productCode: 'Product code is required',
  price: 'Price should be 0 or more',
  vatRange: 'Vat should be more than 0'
};

const imageContainerDimensions: ImageContainerMargins = {
  height: '320px',
  backupMarginTop: 85
};

const NewProduct: React.FunctionComponent<IProps> = ({ handleClose }: IProps) => {
  const productCodes = useProductCodesQuery();
  const membershipTypes = useMembershipTypeOptionsQuery({ fetchPolicy: 'no-cache' });
  const [name, setName] = useState('');
  const [productCode, setProductCode] = useState<IProductCode | null>(null);
  const [membershipType, setMembershipType] = useState<IMembershipType | null>(null);
  const [price, setPrice] = useState('');
  const [vat, setVat] = useState('');
  const [emailBottomNotes, setEmailBottomNotes] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailBanner, setEmailBanner] = useState<string | null | undefined>(null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createProduct, createProductResult] = useCreateProductMutation();
  const { dispatch } = useContext(AppContext);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        name: ErrorTexts.name
      });
    } else {
      setFormErrors({ ...formErrors, name: null });
    }
    setName(value);
  };

  const handleProductCodeChange = (
    _: unknown,
    newValue: React.SetStateAction<IProductCode | null>
  ) => {
    setMembershipType(null);
    if (newValue) {
      setFormErrors({ ...formErrors, productCode: null });
    } else {
      setFormErrors({ ...formErrors, productCode: ErrorTexts.productCode });
    }

    setProductCode(newValue);
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && Number(value) >= 0) {
      setFormErrors({ ...formErrors, price: null });
    } else {
      setFormErrors({
        ...formErrors,
        price: ErrorTexts.price
      });
    }
    setPrice(value);
  };

  const handleVatChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && (Number(value) < 0 || Number(value) > 100)) {
      setFormErrors({
        ...formErrors,
        vatRange: ErrorTexts.vatRange
      });
    } else {
      setFormErrors({ ...formErrors, vatRange: null });
    }
    setVat(value);
  };

  const submitForm = () => {
    const errors = {
      name: null,
      productCode: null,
      price: null,
      vatRange: null
    } as IErrors;

    let hasErrors = false;
    if (!name || name.length < 2) {
      hasErrors = true;
      errors.name = ErrorTexts.name;
    }
    if (!productCode) {
      hasErrors = true;
      errors.productCode = ErrorTexts.productCode;
    }
    if (!price) {
      hasErrors = true;
      errors.price = ErrorTexts.price;
    }
    if (vat && (parseInt(vat) < 0 || parseInt(vat) > 100)) {
      hasErrors = true;
      errors.vatRange = ErrorTexts.vatRange;
    }
    if (!hasErrors && sameProductNameExists(name.trim())) {
      hasErrors = true;
      errors.name = ErrorTexts.nameExists;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      submitProduct();
    }
  };

  const submitProduct = () => {
    dispatch({ type: Actions.HideConfirmation });
    createProduct({
      variables: {
        input: {
          name: name.trim(),
          price: parseFloat(price),
          product_code_id: parseInt(productCode?.id as string),
          vat: parseFloat(vat),
          membership_type_id: membershipType ? Number(membershipType.id) : null,
          email_subject: emailSubject ? emailSubject.trim() : null,
          email_body: emailBody ? emailBody.trim() : null,
          bottom_notes: emailBottomNotes ? emailBottomNotes.trim() : null,
          email_banner: emailBanner ? emailBanner.trim() : null
        }
      },
      update(cache, { data }) {
        if (data?.result) {
          const productQuery = cache.readQuery({
            query: ProductsDocument
          }) as IProductsQuery;
          if (productQuery.result) {
            const cacheProduct = productQuery.result.find((p) => p?.id === data.result.id);
            if (!cacheProduct) {
              const newData = [data.result, ...productQuery.result];
              cache.writeQuery({
                query: ProductsDocument,
                data: { result: newData }
              });
            }
          }
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Product ${data.result.name} created`,
              severity: 'success',
              autoHide: 2000
            }
          });
          handleClose();
        }
      }
    }).catch((error) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          text: error.message,
          severity: 'error',
          autoHide: 6000
        }
      });
    });
  };

  return (
    <DialogBox
      id="new-product"
      width={1400}
      title="Create Product"
      onClose={handleClose}
      childrenSx={{ padding: '40px 80px' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: '60px'
        }}
      >
        <Box
          sx={{
            gridColumn: 'span 2',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '35px'
          }}
        >
          <InputComponent
            id="new-product-name"
            label="Name"
            value={name}
            onChange={handleNameChange}
            disabled={createProductResult.loading}
            errorMsg={formErrors.name}
            additionalStyle={{ gridColumn: 'span 2' }}
          />
          <AutocompleteComponent
            id="new-product-product-code"
            label="Product Code"
            options={productCodes.data?.result || []}
            getOptionLabel={(option: IProductCode) => option.name}
            disabled={createProductResult.loading}
            values={productCode}
            onChange={handleProductCodeChange}
            errorMsg={formErrors.productCode}
          />
          <AutocompleteComponent
            id="new-product-membership-type"
            label="Membership type"
            options={membershipTypes.data?.result.membership_types || []}
            getOptionLabel={(option: IMembershipType) => option?.name}
            disabled={productCode?.name !== RB_NAME || createProductResult.loading}
            values={membershipType}
            onChange={(_, value) => setMembershipType(value)}
          />
          <InputComponent
            id="new-product-price"
            label="Price"
            type="number"
            value={price}
            onChange={handlePriceChange}
            disabled={createProductResult.loading}
            errorMsg={formErrors.price}
          />
          <InputComponent
            id="new-product-vat"
            label="VAT %"
            type="number"
            value={vat}
            onChange={handleVatChange}
            disabled={createProductResult.loading}
            errorMsg={formErrors.vatRange}
            required={false}
          />
          <InputComponent
            id="new-product-email-bottom-notes"
            label="Bottom Notes"
            value={emailBottomNotes}
            onChange={(e) => setEmailBottomNotes(e.currentTarget.value)}
            multiline
            rows={5}
            disabled={createProductResult.loading}
            additionalStyle={{ gridColumn: 'span 2' }}
          />
        </Box>
        <Box sx={{ gridColumn: 'span 2', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <InputComponent
            id="new-product-email-subject"
            label="Email Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.currentTarget.value)}
            disabled={createProductResult.loading}
          />
          <InputComponent
            id="new-product-email-body"
            label="Email Body"
            value={emailBody}
            onChange={(e) => setEmailBody(e.currentTarget.value)}
            multiline
            rows={12}
            disabled={createProductResult.loading}
          />
        </Box>
        <Box id="new-product-email-banner-container">
          <Box id="new-product-email-banner-header">
            <label style={{ color: Colors.main }}>Email Banner</label>
            {emailBanner && (
              <Button
                id="new-product-clear-banner"
                style={{ width: 'auto', float: 'right', marginTop: '-10px' }}
                onClick={() => setEmailBanner(null)}
                disabled={!emailBanner || createProductResult.loading}
              >
                Clear
              </Button>
            )}
          </Box>
          {emailBanner ? (
            <CardMedia
              sx={{ height: imageContainerDimensions.height, mt: '12px' }}
              component="img"
              src={emailBanner as string}
            />
          ) : (
            <EmailBannerUploader
              editable={true}
              loading={createProductResult.loading}
              setImage={setEmailBanner}
              dimensions={imageContainerDimensions}
            />
          )}
        </Box>
      </Box>
      <Box className="centralize" sx={{ marginTop: '60px' }}>
        <CancelButton
          id="cancel-new-product-button"
          disabled={createProductResult.loading}
          onClick={handleClose}
          text="Cancel"
        />
        <SaveButton
          id="create-new-product-button"
          disabled={createProductResult.loading}
          onClick={submitForm}
          text="Create"
        />
      </Box>
    </DialogBox>
  );
};

export default NewProduct;
